import Vue from 'vue'
import VueRouter from 'vue-router'

import store from "@/store"
import useJwt from '@/auth/jwt/useJwt'

import { get } from "lodash"
import { MUTATE_SETTINGS, MUTATE_USER_DATA, MUTATE_LOGIN_STATUS } from "@/store/config/mutation-keys"

// Routes
import { getHomeRouteForLoggedInUser, isUserLoggedIn } from '@/auth/utils'
import { canNavigate, checkLoginTokenStatus } from '@/libs/acl/routeProtection'
import { takeOauthReturn } from '@/libs/oauth-return'
import { beforeEnteringAuthenticatedRoute } from '@/libs/acl/routeMiddlewares'

import authRoutes from "./routes/auth";
import userRoutes from "./routes/user";

Vue.use(VueRouter)

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior() {
    return { x: 0, y: 0 }
  },
  routes: [

    ...authRoutes,
    ...userRoutes,
    {
      path: '*',
      name: 'error-404',
      component: () => import('@/pages/auth/error-404'),
      meta: {
        layout: 'full',
        resource: 'AuthRoute',
        action: 'read',
        roles: ["user", "anonymous"]
      },
    },
  ],
})



router.beforeEach(async (to, from, next) => {
  try {
    // Password reset token validation is handled inside the page component.


    const routesRequiringPasswordSettings = [
      "auth-register",
      "auth-reset-password",
      "auth-forgot-password-mfa"
    ];

    if (routesRequiringPasswordSettings.includes(to.name)) {
      const response = await useJwt.mainService.auth.getRegistrationSettings();
      const { password_complexity_settings } = response.data.data;
      store.commit(`auth/${MUTATE_SETTINGS}`, { password_complexity_settings });
    }

    const isLoggedIn = isUserLoggedIn();

    // P2: an OAuth sign-in that sent the user to log in resumes once they reach home (the login and
    // MFA pages always land there). The authorize page saves the request; see libs/oauth-return.
    if (to.name === 'home' && isLoggedIn) {
      const back = takeOauthReturn();
      if (back) return next(back);
    }

    const toPathRequiresAuth = get(to, 'meta.requireAuth', false);
    const redirectIfLoggedIn = get(to, 'meta.redirectIfLoggedIn', false);

    // console.log('toPathRequiresAuth', toPathRequiresAuth)

    /**
     * path requires authentication but user is logged in.
     */
    if (toPathRequiresAuth && isLoggedIn) {
      await beforeEnteringAuthenticatedRoute(to, from, next)
      return
    }

    /**
     * path requires authentication but user is not logged in.
     */
    if (toPathRequiresAuth && !isLoggedIn) {
      return next({ name: 'auth-login' });
    }

    if (!toPathRequiresAuth && isLoggedIn && redirectIfLoggedIn) {
      console.log("---> requires no authentication with redirect and is logged in");
      const { authorized, user, settings } = await checkLoginTokenStatus()

      const in_maintenance_mode = get(settings, 'enable_maintenance_mode', false)
      if (in_maintenance_mode) {
        if (to.name === 'misc-under-maintenance') {
          return next();
        }
        return next({ name: 'misc-under-maintenance' });
      }

      if (!in_maintenance_mode && to.name === 'misc-under-maintenance') {
        return next("/");
      }

      if (authorized) {
        store.commit(`auth/${MUTATE_LOGIN_STATUS}`, true);
        store.commit(`auth/${MUTATE_USER_DATA}`, user);
        store.commit(`auth/${MUTATE_SETTINGS}`, settings);

        const { name } = getHomeRouteForLoggedInUser(user.user_type);

        const currentUserHomeRoute = router.resolve({ name });
        if (!currentUserHomeRoute) {
          return next();
        }

        if (canNavigate(currentUserHomeRoute.route, user.user_type)) {
          return next({ name, query: get(to, "query") });
        }

        return next({ name: 'misc-not-authorized' })
      }

      // proceed to intended destination because user is not logged in.
      // here user is only redirect if they are logged in.
      store.commit(`auth/${MUTATE_LOGIN_STATUS}`, false);
      return next();
    }

    return next()
  } catch (error) {
    useJwt.mainService.auth.clearAccessToken();

    if (to.name !== "auth-login") {
      store.commit(`auth/${MUTATE_LOGIN_STATUS}`, false)
      next({ name: "auth-login" });
    }

    return next();
  }
})


router.beforeResolve((to, _from, next) => {
  // If this isn't an initial page load.
  if (to.name) {
    // Start the route progress bar.
    // eslint-disable-next-line no-undef
    NProgress.start()
  }
  next()
})

router.afterEach(async (to) => {
  // Complete the animation of the route progress bar.
  // eslint-disable-next-line no-undef
  NProgress.done()

  // Remove initial loading
  const appLoading = document.getElementById('loading-bg')
  if (appLoading) {
    appLoading.style.display = 'none'
  }

  try {
    const requireAuth = get(to, 'meta.requireAuth');
    const hasLogPageName = get(to, 'meta.logPageName');

    const isLoggedIn = isUserLoggedIn();

    if (requireAuth && hasLogPageName && isLoggedIn) {
      await useJwt.mainService.auth.logPageAccess({
        route: to.path,
        comment: hasLogPageName
      });
    }
  } catch (error) {
    //
  }
});


export default router
